import { createTheme } from '@uhg-abyss/web/tools/theme';
import { ThemeProvider } from '@uhg-abyss/web/ui/ThemeProvider';
import React, { useEffect, useRef } from 'react';
import { useReactToPrint } from 'react-to-print';

// Injected into the print document to ensure CJK (Chinese/Japanese/Korean)
// characters are visible. The active web fonts (AbyssOptumSans / AbyssUHCSans)
// have no CJK glyphs, so without this fallback Chinese translations render as
// blank boxes in the printed PDF.
export const CJK_PRINT_FONT_CSS = `
  @font-face {
    font-family: 'PSXCJKPrint';
    src: local('PingFang TC'), local('Hiragino Sans GB'),
         local('Microsoft JhengHei'), local('Noto Sans CJK TC'),
         local('WenQuanYi Micro Hei'), local('Arial Unicode MS');
    unicode-range: U+2E80-2EFF, U+2F00-2FDF, U+3040-9FFF,
                   U+F900-FAFF, U+FE30-FE4F;
  }
  @media print {
    html, body, * {
      font-family: 'AbyssOptumSans', 'AbyssUHCSans', 'PSXCJKPrint',
                   Arial, sans-serif !important;
    }
  }
  @page {
    margin-top: 25mm;
  }
  @page :first {
    margin-top: 12mm;
  }
`;

/**
 * PSXPrintWrapper - A reusable wrapper that enables printing via react-to-print.
 *
 * Usage:
 * 1. Import PSXPrintWrapper and the usePrintPSX hook:
 *    import { PSXPrintWrapper } from '../../common/PSXPrintWrapper';
 *    import { usePrintPSX } from '../../hooks/usePSXPrint';
 *
 * 2. In your component, use the hook to get the print trigger and onReady callback:
 *    const { printPSXPage, onPrintReady } = usePrintPSX();
 *
 * 3. Render the wrapper around the content you want to print:
 *    <PSXPrintWrapper onReady={onPrintReady}>
 *      <PrintView selectedProviderList={results} />
 *    </PSXPrintWrapper>
 *
 * 4. Call printPSXPage() in your button's onClick handler:
 *    <Button onClick={printPSXPage}>Print</Button>
 *
 * Notes:
 * - The wrapper hides its children visually (overflow:hidden, height:0, width:0)
 *   but keeps them in the DOM so react-to-print can access them.
 * - react-to-print copies the content into a hidden iframe for printing and
 *   checks variables against ThemeProvider,
 *   so styles from the page are automatically included.
 * - The print dialog may take 1-2 seconds to appear due to iframe creation
 *   and stylesheet copying — this is expected behavior.
 */

type PSXPrintWrapperProps = {
  children: React.ReactNode;
  onReady?: (printFn: () => void) => void;
};

export const PSXPrintWrapper = ({
  children,
  onReady,
}: PSXPrintWrapperProps) => {
  const contentRef = useRef<HTMLDivElement>(null);

  const theme = createTheme('uhc');

  const docTitle = document.title;
  const handlePrint = useReactToPrint({
    contentRef,
    documentTitle: docTitle || 'PSX Print',
    pageStyle: CJK_PRINT_FONT_CSS,
  });

  useEffect(() => {
    if (onReady) {
      onReady(handlePrint);
    }
  }, []);

  return (
    <div style={{ overflow: 'hidden', height: 0, width: 0 }}>
      <div ref={contentRef}>
        {<ThemeProvider theme={theme}>{children}</ThemeProvider>}
      </div>
    </div>
  );
};

PSXPrintWrapper.displayName = 'PSXPrintWrapper';
